import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Zap, Mail, Lock, ArrowRight } from 'lucide-react';

const Login = () => {
  const [sp] = useSearchParams();
  const expired = sp.get('expired') === '1';
  const next = sp.get('next') || '';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(email, password);
      toast.success('Welcome back!');
      navigate(userData?.role === 'supplier' ? '/portal' : (next.startsWith('/admin') ? next : '/admin'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <img src="/brand/ctc-favicon-64.png" alt="Care Tech Compare" className="w-12 h-12 object-contain" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Care Tech Compare</h1>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">CRM</p>
            </div>
          </div>

          {/* Header */}
          <div className="mb-8">
            {expired && <p className="mb-4 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3" data-testid="login-expired-notice">Your session timed out after 24 hours. Please sign in again.</p>}
            <h2 className="text-4xl font-bold text-gray-900 mb-2">Welcome back</h2>
            <p className="text-gray-600 text-lg">Enter your credentials to access your account</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 text-base font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="pl-11 bg-white border-gray-300 focus:border-blue-500 h-12 text-gray-900 text-base"
                  data-testid="login-email"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-gray-700 text-base font-medium">Password</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-11 bg-white border-gray-300 focus:border-blue-500 h-12 text-gray-900 text-base"
                  data-testid="login-password"
                  required
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 bg-[#00aeef] hover:bg-[#0090c8] text-white shadow-lg text-base font-medium"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Signing in...' : 'Sign in'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>

          <p className="mt-8 text-center text-gray-500 text-sm" data-testid="invite-only-note">
            CRM access is by invitation only. Ask your admin to add you as a team member.
          </p>
        </div>
      </div>

      {/* Right Panel - Visual */}
      <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-[#00aeef] to-[#0077b6] items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1694500069324-d782decdd190?w=1200')] bg-cover bg-center opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#06405c]/60 via-transparent to-transparent" />
        
        <div className="relative z-10 text-center max-w-lg">
          <h2 className="text-5xl font-bold text-white mb-6">
            The CRM built for the care-tech market
          </h2>
          <p className="text-xl text-sky-100 leading-relaxed">
            Reach the right care providers and suppliers with smart segmentation, care-tuned AI outreach, and real-time engagement insights - all in one place.
          </p>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 mt-12">
            <div className="p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <p className="text-3xl font-bold text-white">500+</p>
              <p className="text-sm font-medium text-sky-200 mt-1">Care Tech Products</p>
            </div>
            <div className="p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <p className="text-3xl font-bold text-white">45%</p>
              <p className="text-sm font-medium text-sky-200 mt-1">Reply Rate</p>
            </div>
            <div className="p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <p className="text-3xl font-bold text-white">2x</p>
              <p className="text-sm font-medium text-sky-200 mt-1">Faster Booking</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
